<script setup lang="ts">
/**
 * LeadEditModal — Edit Lead Details Modal
 *
 * Allows sales reps and estimators with STANDARD access or higher
 * to edit prospect information, deal size, priority, and associated records.
 * Uses the design system's BaseModal with native dark/light theme tokens.
 *
 * @see docs/ACUMATICA-INTEGRATION-2.md Section 2 & 4
 */
import { ref, watch } from 'vue'
import { BaseModal, BaseButton } from '@/components'
import {
    LeadPriority,
    type Lead,
    type UpdateLeadDto,
} from '../api/types'

const props = defineProps<{
    modelValue: boolean
    lead: Lead | null
    isSubmitting?: boolean
}>()

const emit = defineEmits<{
    'update:modelValue': [value: boolean]
    save: [payload: UpdateLeadDto]
}>()

const form = ref<UpdateLeadDto>({
    title: '',
    companyName: '',
    primaryContactName: '',
    primaryContactEmail: '',
    primaryContactPhone: '',
    estimatedValue: undefined,
    priority: LeadPriority.MEDIUM,
    description: '',
    propertyId: '',
    contactId: '',
})

watch(
    () => props.lead,
    (curr) => {
        if (curr) {
            form.value = {
                title: curr.title || '',
                companyName: curr.companyName || '',
                primaryContactName: curr.primaryContactName || '',
                primaryContactEmail: curr.primaryContactEmail || '',
                primaryContactPhone: curr.primaryContactPhone || '',
                estimatedValue: curr.estimatedValue,
                priority: curr.priority || LeadPriority.MEDIUM,
                description: curr.description || '',
                propertyId: curr.propertyId || '',
                contactId: curr.contactId || '',
            }
        }
    },
    { immediate: true },
)

const priorityOptions = [
    { value: LeadPriority.LOW, label: 'Low' },
    { value: LeadPriority.MEDIUM, label: 'Medium' },
    { value: LeadPriority.HIGH, label: 'High' },
    { value: LeadPriority.CRITICAL, label: 'Critical' },
]

function handleClose() {
    emit('update:modelValue', false)
}

function handleSave() {
    if (!form.value.title?.trim()) return

    emit('save', {
        title: form.value.title.trim(),
        companyName: form.value.companyName?.trim() || undefined,
        primaryContactName: form.value.primaryContactName?.trim() || undefined,
        primaryContactEmail: form.value.primaryContactEmail?.trim() || undefined,
        primaryContactPhone: form.value.primaryContactPhone?.trim() || undefined,
        estimatedValue: form.value.estimatedValue,
        priority: form.value.priority,
        description: form.value.description?.trim() || undefined,
        propertyId: form.value.propertyId?.trim() || undefined,
        contactId: form.value.contactId?.trim() || undefined,
    })
}
</script>

<template>
    <BaseModal
        :model-value="modelValue"
        title="Edit Lead Details"
        size="lg"
        @update:model-value="(v) => emit('update:modelValue', v)"
    >
        <form id="lead-edit-form" class="edit-form" @submit.prevent="handleSave">
            <div class="form-group">
                <label class="form-label" for="edit-title">
                    Lead Title <span class="required">*</span>
                </label>
                <input
                    id="edit-title"
                    v-model="form.title"
                    type="text"
                    required
                    class="form-input"
                    placeholder="Inquired Scope or Project Title"
                />
            </div>

            <div class="form-row">
                <div class="form-group">
                    <label class="form-label" for="edit-company">Company / Account Name</label>
                    <input
                        id="edit-company"
                        v-model="form.companyName"
                        type="text"
                        class="form-input"
                        placeholder="Company Name"
                    />
                </div>
                <div class="form-group">
                    <label class="form-label" for="edit-priority">Priority</label>
                    <select
                        id="edit-priority"
                        v-model="form.priority"
                        class="form-input form-select"
                    >
                        <option v-for="opt in priorityOptions" :key="opt.value" :value="opt.value">
                            {{ opt.label }}
                        </option>
                    </select>
                </div>
            </div>

            <div class="form-row">
                <div class="form-group">
                    <label class="form-label" for="edit-contact-name">Primary Contact Name</label>
                    <input
                        id="edit-contact-name"
                        v-model="form.primaryContactName"
                        type="text"
                        class="form-input"
                        placeholder="Jane Miller"
                    />
                </div>
                <div class="form-group">
                    <label class="form-label" for="edit-value">Estimated Deal Value ($ USD)</label>
                    <input
                        id="edit-value"
                        v-model.number="form.estimatedValue"
                        type="number"
                        min="0"
                        step="100"
                        class="form-input"
                        placeholder="125000"
                    />
                </div>
            </div>

            <div class="form-row">
                <div class="form-group">
                    <label class="form-label" for="edit-contact-email">Email Address</label>
                    <input
                        id="edit-contact-email"
                        v-model="form.primaryContactEmail"
                        type="email"
                        class="form-input"
                        placeholder="name@domain.com"
                    />
                </div>
                <div class="form-group">
                    <label class="form-label" for="edit-contact-phone">Phone Number</label>
                    <input
                        id="edit-contact-phone"
                        v-model="form.primaryContactPhone"
                        type="tel"
                        class="form-input"
                        placeholder="+1 (555) 0199"
                    />
                </div>
            </div>

            <div class="form-group">
                <label class="form-label" for="edit-description">Scope & Notes</label>
                <textarea
                    id="edit-description"
                    v-model="form.description"
                    rows="3"
                    class="form-input form-textarea"
                    placeholder="Add or update notes..."
                />
            </div>

            <div class="form-row">
                <div class="form-group">
                    <label class="form-label" for="edit-prop-id">Associated Property ID</label>
                    <input
                        id="edit-prop-id"
                        v-model="form.propertyId"
                        type="text"
                        class="form-input"
                        placeholder="UUID (optional)"
                    />
                </div>
                <div class="form-group">
                    <label class="form-label" for="edit-contact-id">Associated Contact ID</label>
                    <input
                        id="edit-contact-id"
                        v-model="form.contactId"
                        type="text"
                        class="form-input"
                        placeholder="UUID (optional)"
                    />
                </div>
            </div>
        </form>

        <template #footer>
            <div class="modal-actions">
                <button type="button" class="action-link" @click="handleClose">
                    Cancel
                </button>
                <BaseButton
                    type="submit"
                    form="lead-edit-form"
                    variant="primary"
                    :loading="isSubmitting"
                    @click="handleSave"
                >
                    Save Changes
                </BaseButton>
            </div>
        </template>
    </BaseModal>
</template>

<style scoped>
.edit-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
}

.form-label {
    font-size: var(--text-xs);
    font-weight: 500;
    color: var(--text-muted);
}

.required {
    color: var(--color-error);
}

.form-input {
    padding: 0.5rem 0.625rem;
    background: var(--input-bg);
    border: 1px solid var(--input-border);
    border-radius: var(--radius-sm);
    color: var(--text-primary);
    font-size: var(--text-sm);
    font-family: inherit;
    transition: border-color 0.15s, box-shadow 0.15s;
}

.form-input:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(18, 192, 221, 0.15);
}

.form-select {
    cursor: pointer;
}

.form-textarea {
    resize: vertical;
    font-family: inherit;
    min-height: 72px;
}

.modal-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
    width: 100%;
}

.action-link {
    background: none;
    border: none;
    color: var(--text-secondary);
    font-size: var(--text-sm);
    cursor: pointer;
    padding: 0.375rem 0.75rem;
    transition: color 0.15s;
}

.action-link:hover {
    color: var(--text-primary);
}

@media (max-width: 640px) {
    .form-row {
        grid-template-columns: 1fr;
    }
}
</style>
